import { Injectable, OnDestroy, Signal, resource } from '@angular/core';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  DynamicQueryService,
} from '@services/analytics-services/core';
import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import {
  GroupChartTabs,
  GroupedTabData,
} from '@services/analytics-services/core/group-chart.modals';
import {
  GroupedDataState,
  GroupedDataStoreService,
} from '@services/analytics-services/core/grouped-data-store.service';
import { ChartType, QueryType } from '@services/analytics-services/core/models/matrix-stats';
import { STATUS_COLORS } from '@services/analytics-services/hight-chart-configurations';
import { ChartOptions } from '@services/analytics-services/hight-chart-configurations/high-chart-config';
import { ShippingAnalyticsService, ShippingStats } from '@services/analytics-services/shipping';
import { ShippingAnalyticsCharts } from '@services/analytics-services/shipping/shipping-analytics.charts';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import { DateRangePresetType, Pagination, ProcessingState } from '@services/core-services/models';
import { BehaviorSubject, Observable, Subject, catchError, of, tap, firstValueFrom } from 'rxjs';

@Injectable()
export class DashboardShipmentStore implements OnDestroy {
  private destroy$ = new Subject<void>();

  private stateWiseShipmentPerformaceState: GroupedDataState;
  private errorMessage = new BehaviorSubject<string>('');

  constructor(
    private readonly shippingAnalyticsService: ShippingAnalyticsService,
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly groupedDataStoreService: GroupedDataStoreService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly shippingAnalyticsCharts: ShippingAnalyticsCharts
  ) {
    this.stateWiseShipmentPerformaceState =
      this.groupedDataStoreService.createGroupedDataState('state');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getStateWiseShipmentPerformanceStats(): Observable<GroupedTabData> {
    return this.stateWiseShipmentPerformaceState.stats.asObservable();
  }

  getStateWiseShipmentPerformanceTabs(): Observable<GroupChartTabs[]> {
    return this.stateWiseShipmentPerformaceState.tabs.asObservable();
  }

  getStateWiseShipmentPerformanceTabsActiveTab(): Observable<string> {
    return this.stateWiseShipmentPerformaceState.defaultTab.asObservable();
  }

  getStateWiseShipmentPerformancePagination(): Observable<Pagination> {
    return this.stateWiseShipmentPerformaceState.pagination.asObservable();
  }

  getStateWiseShipmentPerformanceLoader(): Observable<ProcessingState> {
    return this.stateWiseShipmentPerformaceState.loader.asObservable();
  }

  getErrorMessage(): Observable<string> {
    return this.errorMessage.asObservable();
  }

  loadStateWiseShipmentPerformanceData(context: AnalyticsRangeContext): Observable<any> {
    this.stateWiseShipmentPerformaceState.loader.next(ProcessingState.Loading);
    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: context.preset as DateRangePresetType,
        date_from: context.range.start,
        date_to: context.range.end,
        interval: context.interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.OVER_ALL_SHIPPING_PERFORMANCE,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap(response => {
          if (!response || response.length === 0) {
            this.stateWiseShipmentPerformaceState.loader.next(ProcessingState.Empty);
            return;
          }
          const data = this.extractResponseList(response);
          this.groupedDataStoreService.processEntityGroupedData(
            this.stateWiseShipmentPerformaceState,
            data,
            'state',
            'overallShippingPerformance'
          );
        }),
        catchError(error => {
          console.error(`Error loading state-wise performance data:`, error);
          this.stateWiseShipmentPerformaceState.loader.next(ProcessingState.Error);
          this.handleError('Error loading state-wise shipping performance', error);
          return of({ list: [] });
        })
      );
  }

  // Utility methods for conversion and mapping
  formatTooltipValue = (metric: any): string => {
    if (!metric) return '';

    // Get the value and check if it's a time-based metric
    const isTimeMetric = ['avg_delivery_time', 'avg_time_to_ship', 'avg_transit_time'].includes(
      metric.key
    );

    const value = metric.value;

    if (isTimeMetric && typeof value === 'number') {
      return `${value.toFixed(1)} days`;
    }

    // For regular metrics (Shipped, Early, On time, Late)
    if (typeof value === 'number') {
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}k`;
      }
      return `${value}`;
    }

    return '';
  };

  mapTabIdToMetric(tabId: string): string {
    const mapping: Record<string, string> = {
      delivery: 'avg_delivery_time',
      ship: 'avg_time_to_ship',
      transit: 'avg_transit_time',
      // add more as needed
    };
    return mapping[tabId] || 'avg_delivery_time';
  }

  updateStatePerformanceDefaultTab(tabId: string): void {
    this.stateWiseShipmentPerformaceState.defaultTab.next(tabId);
    this.groupedDataStoreService.updatePagination(this.stateWiseShipmentPerformaceState, tabId, 1);
  }

  updateStatePerformancePagination(tabId: string, page: number, limit: number = 8): void {
    this.groupedDataStoreService.updatePagination(
      this.stateWiseShipmentPerformaceState,
      tabId,
      page,
      limit
    );
  }

  private extractResponseList(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      if (response.list && Array.isArray(response.list)) {
        return response.list;
      } else if (response.stats && Array.isArray(response.stats)) {
        return response.stats;
      } else if (response.content?.list && Array.isArray(response.content.list)) {
        return response.content.list;
      } else if (response.content?.stats && Array.isArray(response.content.stats)) {
        return response.content.stats;
      }
    }
    console.warn('Could not extract list from response:', response);
    return [];
  }

  private handleError(message: string, error: any): void {
    const errorMessage = this.errorHandlerService.handleServerError(error);
    this.errorMessage.next(typeof errorMessage === 'string' ? errorMessage : message);
    console.error(message, error);
  }

  /**
   * Creates a resource for loading shipment analytics data
   */
  public createShipmentAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: ({ params }): Promise<AnalyticsInfo<ShippingStats>> => {
        const { range, compare, interval, preset } = params;
        return firstValueFrom(
          this.shippingAnalyticsService.getAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
          })
        );
      },
    });
  }

  /**
   * Creates a resource for loading shipping SLA chart data
   */
  public createShippingSLAChartResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<ChartOptions | undefined> => {
        const response = await firstValueFrom(
          this.shippingAnalyticsService.getShippingSLAStats(params)
        );
        const list = response?.list || [];
        if (list.length === 0) {
          return undefined;
        }

        const chartData = list.map((item: { slo_status: string; shipment_count: number }) => ({
          label: item.slo_status,
          y: item.shipment_count,
          color: STATUS_COLORS[item.slo_status] || STATUS_COLORS['neutral'],
        }));

        return {
          data: chartData,
          title: 'Delivery Status Distribution',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${point.name}</h4>
                <span class="dash-border w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                    <div class="h4-reg primary-50">Shipments</div>
                  </div>
                  <div class="h4-bold primary-0">${point.y}</div>
                </div>
              </div>
            </div>
          `,
        };
      },
    });
  }

  /**
   * Creates a resource for loading shipments by courier chart data
   */
  public createShipmentsByCourierResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<ChartOptions | undefined> => {
        const data = await firstValueFrom(
          this.shippingAnalyticsCharts.getShippingByCourierCharts(params)
        );
        if (!data || data.length === 0) {
          return undefined;
        }

        return {
          categories: data.map(item => item.label ?? 'null'),
          data: data.map(item => ({
            y: item.value,
            label: item.label,
          })),
          yAxisTitle: 'Number of Shipments',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${point.options.label}</h4>
                <span class="dash-border w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold"> </div>
                    <div class="h4-reg primary-50">Shipped Order</div>
                  </div>
                  <div class="h4-bold primary-0">${point.options.y}</div>
                </div>
              </div>
            </div>
          `,
        };
      },
    });
  }

  /**
   * Creates a resource for loading average delivery time chart data
   */
  public createAverageDeliveryTimeResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<ChartOptions | undefined> => {
        const data = await firstValueFrom(
          this.shippingAnalyticsCharts.getAverageDeliveryByCourierCharts(params)
        );
        if (!data || data.length === 0) {
          return undefined;
        }

        return {
          categories: data.map(item => item.label ?? 'null'),
          data: data.map(item => ({
            y: item.value,
            label: item.label,
          })),
          yAxisTitle: 'Average Days',
          tooltipFormatter: (point: Highcharts.Point) => `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${point.options.label}</h4>
                <span class="dash-border w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point.color}" class="tooltipDot d-flex align-items-center h4-bold"> </div>
                    <div class="h4-reg primary-50">Average Delivery Time</div>
                  </div>
                  <div class="h4-bold primary-0">${point.y} days</div>
                </div>
              </div>
            </div>
          `,
        };
      },
    });
  }
}
